import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { ArrowUp, Linkedin, Instagram, Github, Twitter, Dribbble } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PremiumHeader from '@/components/PremiumHeader';
import { siteConfig as staticConfig } from '@/lib/config';
import { useSiteConfig } from '@/hooks/use-site-settings';

interface LayoutProps {
  children?: React.ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();

  // Fetch dynamic config
  const { data: dynamicConfig } = useSiteConfig();

  // Merge configs (prefer dynamic, fallback to static)
  const config = {
    name: dynamicConfig?.site_name || staticConfig.name,
    description: dynamicConfig?.site_description || 'Transformando ideias em experiências visuais únicas e memoráveis.',
    email: dynamicConfig?.contact_email || staticConfig.email,
    phone: dynamicConfig?.contact_phone || staticConfig.phone,
    social: {
      linkedin: dynamicConfig?.social_links?.linkedin || staticConfig.social.linkedin,
      instagram: dynamicConfig?.social_links?.instagram || staticConfig.social.instagram,
      github: dynamicConfig?.social_links?.github || '',
      twitter: dynamicConfig?.social_links?.twitter || '',
      dribbble: dynamicConfig?.social_links?.dribbble || staticConfig.social.dribbble,
      behance: dynamicConfig?.social_links?.behance || staticConfig.social.behance,
    }
  };

  // Dark mode - always enabled
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  // Scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Skip Link para acessibilidade */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md"
      >
        Pular para conteúdo principal
      </a>

      {/* Header */}
      <PremiumHeader />

      {/* Main Content */}
      <main id="main-content" className="pt-16">
        {children || <Outlet />}
      </main>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-40 rounded-full w-12 h-12 p-0 shadow-lg"
          aria-label="Voltar ao topo"
        >
          <ArrowUp className="h-5 w-5" />
        </Button>
      )}

      {/* Footer */}
      <footer className="bg-muted/50 border-t border-border">
        <div className="container mx-auto container-padding py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <span className="font-display text-xl font-bold gradient-text">
                  {config.name}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {config.description}
              </p>
            </div>

            {/* Quick Links */}
            <div className="space-y-4">
              <h3 className="font-semibold">Links Rápidos</h3>
              <nav className="flex flex-col space-y-2">
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Início
                </Link>
                <Link to="/projetos" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Projetos
                </Link>
                <Link to="/servicos" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Serviços
                </Link>
                <Link to="/sobre" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Sobre
                </Link>
              </nav>
            </div>

            {/* Services */}
            <div className="space-y-4">
              <h3 className="font-semibold">Serviços</h3>
              <nav className="flex flex-col space-y-2">
                <Link to="/servicos" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Branding
                </Link>
                <Link to="/servicos" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  UI/UX Design
                </Link>
                <Link to="/servicos" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Motion Graphics
                </Link>
                <Link to="/servicos" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Ilustração
                </Link>
              </nav>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h3 className="font-semibold">Contato</h3>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Vamos criar algo incrível juntos?
                </p>
                <Button size="sm" className="w-full">
                  <Link to="/contato">Falar comigo</Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-border mt-8 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} {config.name}. Todos os direitos reservados.
              </p>
              <div className="flex items-center space-x-6">
                {config.social.linkedin && (
                  <a href={config.social.linkedin} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="LinkedIn">
                    <Linkedin className="h-5 w-5" />
                  </a>
                )}
                {config.social.instagram && (
                  <a href={config.social.instagram} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Instagram">
                    <Instagram className="h-5 w-5" />
                  </a>
                )}
                {config.social.github && (
                  <a href={config.social.github} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="GitHub">
                    <Github className="h-5 w-5" />
                  </a>
                )}
                {config.social.twitter && (
                  <a href={config.social.twitter} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Twitter">
                    <Twitter className="h-5 w-5" />
                  </a>
                )}
                {config.social.dribbble && (
                  <a href={config.social.dribbble} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Dribbble">
                    <Dribbble className="h-5 w-5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;